import React, { useState, useEffect, useRef } from 'react'
import { useReactToPrint } from 'react-to-print'
import { API_BASE, ROUTER_BASENAME } from '../config'
import { mediaUrl, hideBrokenImg } from '../utils/mediaUrl'

const DEFAULT_RECEIPT = {
  receipt_name: 'श्री आचार्य विद्यासागर जीवदया ट्रस्ट',
  receipt_address: 'वीतराग,सेक.२४,प्लाट नंबर ६,निगड़ी प्राधिकरण,निगड़ी, पुणे - ४११०४४',
  receipt_registration_number: 'महा. ३७०/२०१४/पुणे',
  receipt_website: 'www.jeevdaya.net',
  footer_contact: '',
  logo_path: '',
}

const ASSET_BASE = (ROUTER_BASENAME || '/arihant_trust').replace(/\/$/, '')
const FALLBACK_LOGO = `${ASSET_BASE}/assets/images/logo.png`

const C = {
  maroon: '#800000',
  burgundy: '#5C0000',
  gold: '#FFCC00',
  goldBright: '#FFD700',
  cream: '#FFF9E5',
  accentRed: '#C41E3A',
  ink: '#2A1A1A',
  softMaroon: '#F5E6E6',
}

function formatDate(value) {
  if (!value) return ''
  const s = String(value)
  if (/^\d{2}[-/]\d{2}[-/]\d{4}/.test(s)) return s.split(/[ T]/)[0]
  const d = new Date(s)
  if (!Number.isNaN(d.getTime())) {
    const dd = String(d.getDate()).padStart(2, '0')
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    return `${dd}-${mm}-${d.getFullYear()}`
  }
  return s
}

function buildAddress(data) {
  if (!data) return ''
  const parts = [
    data.address1,
    data.address2,
    data.city,
    data.state_name || data.state,
    data.zip_no,
  ].filter((p) => p != null && String(p).trim() !== '')
  return parts.join(', ')
}

function PaymentReceipt(props) {
  const componentRef = useRef()
  const [receipt, setReceipt] = useState(DEFAULT_RECEIPT)
  const data = props.data || {}

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `receipt-${data.order_id || data.payment_id || 'donation'}`,
    onAfterPrint: () => alert('Print Success'),
  })

  useEffect(() => {
    let cancelled = false
    fetch(`${API_BASE}/settings/get`)
      .then((res) => res.json())
      .then((payload) => {
        if (cancelled || payload.status !== 'success' || !payload.response) return
        const r = payload.response
        setReceipt({
          receipt_name: r.receipt_name || r.website_name || DEFAULT_RECEIPT.receipt_name,
          receipt_address: r.receipt_address || DEFAULT_RECEIPT.receipt_address,
          receipt_registration_number:
            r.receipt_registration_number || DEFAULT_RECEIPT.receipt_registration_number,
          receipt_website: r.receipt_website || DEFAULT_RECEIPT.receipt_website,
          footer_contact: r.footer_contact || '',
          logo_path: r.logo_path || '',
        })
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  const logoSrc = mediaUrl(receipt.logo_path) || FALLBACK_LOGO
  const donorAddress = data.address || buildAddress(data)
  const donationLabel =
    data.type_name || data.donation_type_name || data.donation_type || 'Donation'
  const amountWords = data.amount_in_words || ''
  const receiptNo = data.order_id || data.payment_id || '—'
  const receiptDate = formatDate(data.created_at || data.date_added)
  const pan = data.pan || data.pan_number || data.panNumber || ''
  const aadhaar = data.adhar_card || data.adharCard || data.aadhaar || ''
  const notes = [
    data.mode_of_payment ? `Mode: ${data.mode_of_payment}` : '',
    data.transaction_id ? `Txn: ${data.transaction_id}` : '',
    data.mode_of_payment === 'Cheque' && data.bank_name ? `Bank: ${data.bank_name}` : '',
    data.mode_of_payment === 'Cheque' && data.cheque_no ? `Chq: ${data.cheque_no}` : '',
  ]
    .filter(Boolean)
    .join(' · ')

  const modalStyle = {
    display: 'block',
    backgroundColor: 'rgba(0,0,0,0.8)',
  }

  return (
    <>
      <div className="modal" style={modalStyle}>
        <div className="modal-dialog modal-xl modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <b>Payment Receipt</b>
              </h5>
              <button className="btn-close" type="button" onClick={props.hide}>
                <i className="fa fa-close"></i>
              </button>
            </div>
            <div className="modal-body">
              <div ref={componentRef} className="receipt-print-root">
                <style>{`
                  @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+Devanagari:wght@400;600;700&family=Noto+Serif+Devanagari:wght@600;700&display=swap');

                  .receipt-print-root {
                    width: 100%;
                    max-width: 210mm;
                    margin: 0 auto;
                    font-family: 'Noto Sans Devanagari', 'Noto Serif Devanagari', Mangal, Kokila, 'Segoe UI', Arial, sans-serif;
                    color: ${C.ink};
                    background: ${C.cream};
                  }
                  .receipt-sheet {
                    background: ${C.cream};
                    border: 2px solid ${C.maroon};
                    overflow: hidden;
                    position: relative;
                  }
                  .receipt-header {
                    background: linear-gradient(180deg, ${C.burgundy} 0%, ${C.maroon} 100%);
                    color: #fff;
                    padding: 10px 14px 6px;
                    text-align: center;
                    position: relative;
                  }
                  .receipt-meta {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    font-size: 11px;
                    letter-spacing: 0.02em;
                    margin-bottom: 4px;
                  }
                  .receipt-meta span { flex: 1; }
                  .receipt-meta .meta-center { text-align: center; font-weight: 700; font-size: 13px; }
                  .receipt-meta .meta-right { text-align: right; }
                  .receipt-title {
                    color: ${C.goldBright};
                    font-family: 'Noto Serif Devanagari', 'Noto Sans Devanagari', Mangal, serif;
                    font-size: 22px;
                    font-weight: 700;
                    line-height: 1.25;
                    margin: 4px 0 2px;
                    text-shadow: 0 1px 0 rgba(0,0,0,0.25);
                  }
                  .receipt-address {
                    color: #fff;
                    font-size: 11px;
                    line-height: 1.35;
                    white-space: pre-line;
                    opacity: 0.95;
                    max-width: 92%;
                    margin: 0 auto 4px;
                  }
                  .receipt-scallop {
                    display: block;
                    width: 100%;
                    height: 14px;
                    margin: 0;
                    line-height: 0;
                  }
                  .receipt-body {
                    position: relative;
                    padding: 14px 18px 16px;
                    min-height: 420px;
                  }
                  .receipt-watermark {
                    position: absolute;
                    left: 50%;
                    top: 48%;
                    transform: translate(-50%, -50%);
                    width: 280px;
                    max-width: 55%;
                    opacity: 0.08;
                    pointer-events: none;
                    z-index: 0;
                  }
                  .receipt-body > *:not(.receipt-watermark) { position: relative; z-index: 1; }
                  .receipt-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: baseline;
                    margin-bottom: 12px;
                    font-size: 13px;
                  }
                  .receipt-label {
                    color: ${C.maroon};
                    font-weight: 700;
                  }
                  .receipt-value-accent {
                    color: ${C.accentRed};
                    font-weight: 700;
                    margin-left: 6px;
                    letter-spacing: 0.03em;
                  }
                  .donor-line {
                    display: flex;
                    align-items: flex-end;
                    gap: 8px;
                    margin-bottom: 10px;
                    font-size: 13px;
                    min-height: 22px;
                  }
                  .donor-line .lbl {
                    color: ${C.maroon};
                    font-weight: 700;
                    white-space: nowrap;
                    flex-shrink: 0;
                  }
                  .donor-line .val {
                    flex: 1;
                    border-bottom: 1px solid ${C.maroon};
                    padding: 0 4px 2px;
                    min-height: 18px;
                    color: ${C.ink};
                  }
                  .receipt-table {
                    width: 100%;
                    border-collapse: collapse;
                    margin: 14px 0 12px;
                    font-size: 12px;
                    background: transparent;
                  }
                  .receipt-table th,
                  .receipt-table td {
                    border: 1px solid ${C.maroon};
                    padding: 8px 10px;
                    vertical-align: top;
                  }
                  .receipt-table th {
                    background: ${C.softMaroon};
                    color: ${C.maroon};
                    font-weight: 700;
                    text-align: center;
                  }
                  .receipt-table .col-part { width: 42%; color: ${C.maroon}; font-weight: 600; }
                  .receipt-table .col-amt { width: 22%; text-align: right; font-weight: 700; }
                  .receipt-table .col-notes { width: 36%; font-size: 11px; }
                  .receipt-table .total-row td {
                    background: ${C.softMaroon};
                    color: ${C.maroon};
                    font-weight: 700;
                  }
                  .receipt-footer-grid {
                    display: flex;
                    justify-content: space-between;
                    gap: 24px;
                    margin-top: 18px;
                    font-size: 12px;
                  }
                  .sig-block { flex: 1; }
                  .sig-block.right { text-align: right; }
                  .sig-space {
                    height: 48px;
                    margin-bottom: 4px;
                  }
                  .sig-line {
                    border-top: 1px solid ${C.maroon};
                    display: inline-block;
                    min-width: 160px;
                    padding-top: 4px;
                    color: ${C.maroon};
                    font-weight: 700;
                  }
                  .receipt-notes {
                    margin-top: 16px;
                    padding-top: 8px;
                    border-top: 1px solid rgba(128,0,0,0.25);
                    font-size: 10px;
                    line-height: 1.45;
                    color: #333;
                  }
                  .receipt-notes p { margin: 0 0 4px; }
                  @media print {
                    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                    .receipt-print-root { max-width: none; }
                    .receipt-sheet { border-width: 1.5pt; }
                  }
                `}</style>

                <div className="receipt-sheet">
                  <div className="receipt-header">
                    <div className="receipt-meta">
                      <span>
                        Reg. No.: {receipt.receipt_registration_number}
                      </span>
                      <span className="meta-center">|| श्री ||</span>
                      <span className="meta-right">
                        {receipt.footer_contact
                          ? `Mob.: ${receipt.footer_contact}`
                          : receipt.receipt_website
                            ? `Visit: ${receipt.receipt_website}`
                            : ''}
                      </span>
                    </div>
                    <div className="receipt-title">{receipt.receipt_name}</div>
                    <div className="receipt-address">{receipt.receipt_address}</div>
                  </div>

                  <svg
                    className="receipt-scallop"
                    viewBox="0 0 800 14"
                    preserveAspectRatio="none"
                    aria-hidden="true"
                  >
                    <rect width="800" height="14" fill={C.maroon} />
                    <path
                      d="M0 0 Q12.5 14 25 0 Q37.5 14 50 0 Q62.5 14 75 0 Q87.5 14 100 0 Q112.5 14 125 0 Q137.5 14 150 0 Q162.5 14 175 0 Q187.5 14 200 0 Q212.5 14 225 0 Q237.5 14 250 0 Q262.5 14 275 0 Q287.5 14 300 0 Q312.5 14 325 0 Q337.5 14 350 0 Q362.5 14 375 0 Q387.5 14 400 0 Q412.5 14 425 0 Q437.5 14 450 0 Q462.5 14 475 0 Q487.5 14 500 0 Q512.5 14 525 0 Q537.5 14 550 0 Q562.5 14 575 0 Q587.5 14 600 0 Q612.5 14 625 0 Q637.5 14 650 0 Q662.5 14 675 0 Q687.5 14 700 0 Q712.5 14 725 0 Q737.5 14 750 0 Q762.5 14 775 0 Q787.5 14 800 0 L800 14 L0 14 Z"
                      fill={C.gold}
                    />
                  </svg>

                  <div className="receipt-body">
                    <img
                      className="receipt-watermark"
                      src={logoSrc}
                      alt=""
                      onError={hideBrokenImg}
                    />

                    <div className="receipt-row">
                      <div>
                        <span className="receipt-label">Receipt No.:</span>
                        <span className="receipt-value-accent">{receiptNo}</span>
                      </div>
                      <div>
                        <span className="receipt-label">Date:</span>
                        <span className="receipt-value-accent">{receiptDate}</span>
                      </div>
                    </div>

                    <div className="donor-line">
                      <span className="lbl">Received with thanks from</span>
                      <span className="val">{data.name || ''}</span>
                    </div>
                    <div className="donor-line">
                      <span className="lbl">Address</span>
                      <span className="val">{donorAddress}</span>
                    </div>
                    <div className="donor-line">
                      <span className="lbl">Mobile</span>
                      <span className="val">{data.mobile || ''}</span>
                      {pan ? (
                        <>
                          <span className="lbl">PAN</span>
                          <span className="val">{pan}</span>
                        </>
                      ) : null}
                      {aadhaar ? (
                        <>
                          <span className="lbl">Aadhaar</span>
                          <span className="val">{aadhaar}</span>
                        </>
                      ) : null}
                    </div>
                    <div className="donor-line">
                      <span className="lbl">Amount in words</span>
                      <span className="val">
                        {amountWords
                          ? `${amountWords}${/only$/i.test(amountWords) ? '' : ' Only'}`
                          : ''}
                      </span>
                    </div>

                    <table className="receipt-table">
                      <thead>
                        <tr>
                          <th>Particulars</th>
                          <th>Amount (₹)</th>
                          <th>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="col-part">{donationLabel}</td>
                          <td className="col-amt">
                            {data.payment_amount != null && data.payment_amount !== ''
                              ? Number(data.payment_amount).toLocaleString('en-IN')
                              : ''}
                          </td>
                          <td className="col-notes">{notes}</td>
                        </tr>
                        <tr className="total-row">
                          <td className="col-part">Total</td>
                          <td className="col-amt">
                            {data.payment_amount != null && data.payment_amount !== ''
                              ? `₹ ${Number(data.payment_amount).toLocaleString('en-IN')}`
                              : ''}
                          </td>
                          <td className="col-notes" />
                        </tr>
                      </tbody>
                    </table>

                    <div className="receipt-footer-grid">
                      <div className="sig-block">
                        <div className="sig-space" />
                        <span className="sig-line">Donor&apos;s Signature</span>
                      </div>
                      <div className="sig-block right">
                        <div className="sig-space" />
                        <span className="sig-line">Manager / Authorised Signatory</span>
                      </div>
                    </div>

                    <div className="receipt-notes">
                      <p>
                        <b>80G:</b> Registered under Income Tax Act Section 80G — DI No.
                        AAO TS 1209 HE 2021901 / UR No. AAO TS 1209 HE 20219; DI No. AAO
                        TS 1209 HF 2021701 / UR No. AAO TS 1209 HF 20217.
                      </p>
                      {receipt.receipt_website ? (
                        <p>
                          Visit us: {receipt.receipt_website}
                          {receipt.footer_contact ? ` · Contact: ${receipt.footer_contact}` : ''}
                        </p>
                      ) : null}
                      {data.mode_of_payment === 'Cheque' ? (
                        <p>* Subject to realisation of Cheque.</p>
                      ) : null}
                      <p>
                        This is a computer-generated donation receipt. Please retain for
                        your records.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <center className="mt-3">
                <button
                  type="button"
                  onClick={handlePrint}
                  className="btn btn-rounded btn-primary"
                >
                  Download PDF
                </button>
              </center>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default PaymentReceipt
